import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Universal Chart Data Interface
interface ChartSeries {
  name: string;
  data: number[];
  stack?: string;
}

interface UniversalChartData {
  type: 'bar' | 'stackedColumn';
  title?: string;
  subtitle?: string;
  categories: string[];
  series: ChartSeries[];
  xAxisTitle?: string;
  yAxisTitle?: string;
  stacking?: 'normal';
  showLegend?: boolean;
  colors?: string[];
  showDataLabels?: boolean;
  labelsMode?: 'values' | 'percentages' | 'both' | 'none';
}

interface BarChartProps {
  data: UniversalChartData;
  showDataLabels?: boolean;
  labelsMode?: 'values' | 'percentages' | 'both' | 'none';
  containerHeight?: number;
  maxCategories?: number;
}

interface ProcessedDataPoint {
  label: string;
  value: number;
  displayValue: string;
  legend?: string;
  currency?: string;
  name?: string;
  stack?: string;
}

const formatValue = (value: number | string | undefined): string => {
  if (value === undefined || value === null || value === '') {
    return '0';
  }

  let numValue: number;
  if (typeof value === 'string') {
    const cleanedValue = value.replace(/[,\s]/g, '');
    numValue = parseFloat(cleanedValue);
  } else {
    numValue = Number(value);
  }

  if (isNaN(numValue) || !isFinite(numValue)) {
    return '0';
  }

  if (numValue >= 1000000000) {
    return (numValue / 1000000000).toFixed(1) + 'B';
  } else if (numValue >= 1000000) {
    return (numValue / 1000000).toFixed(1) + 'M';
  } else if (numValue >= 1000) {
    return (numValue / 1000).toFixed(1) + 'K';
  }
  return Math.round(numValue).toString();
};

const sortDataByValue = (data: UniversalChartData, maxCategories: number = 100): UniversalChartData => {
  if (!data || !data.series || data.series.length === 0 || !data.categories) {
    return data;
  }

  const isStackChart = data.type === 'stackedColumn';

  // Create array of indices with their corresponding total values for sorting
  const indexValuePairs = data.categories.map((category, index) => {
    let totalValue = 0;

    if (isStackChart) {
      // For stacked charts, sum all series values for this category
      totalValue = data.series.reduce((sum, series) => {
        return sum + (series.data[index] || 0);
      }, 0);
    } else {
      // For regular charts, use the first (or only) series
      totalValue = data.series[0]?.data[index] || 0;
    }

    return {
      index,
      category,
      totalValue
    };
  });

  // Sort by total value in descending order (highest first)
  indexValuePairs.sort((a, b) => b.totalValue - a.totalValue);

  // If we have more categories than maxCategories, group the rest into "Other"
  let finalCategories: string[];
  let finalSeries: ChartSeries[];

  if (indexValuePairs.length <= maxCategories) {
    // No need to group, use all categories
    finalCategories = indexValuePairs.map(pair => pair.category);
    finalSeries = data.series.map(series => ({
      ...series,
      data: indexValuePairs.map(pair => series.data[pair.index] || 0)
    }));
  } else {
    // Group remaining categories into "Other"
    const topCategories = indexValuePairs.slice(0, maxCategories);
    const remainingCategories = indexValuePairs.slice(maxCategories);

    // Create categories array with top categories + "Other"
    finalCategories = [...topCategories.map(pair => pair.category), 'Other'];

    // Create series data with aggregated "Other" values
    finalSeries = data.series.map(series => {
      const topData = topCategories.map(pair => series.data[pair.index] || 0);

      // Sum up all remaining values for "Other"
      const otherValue = remainingCategories.reduce((sum, pair) => {
        return sum + (series.data[pair.index] || 0);
      }, 0);

      return {
        ...series,
        data: [...topData, otherValue]
      };
    });
  }

  return {
    ...data,
    categories: finalCategories,
    series: finalSeries
  };
};

const BarChart: React.FC<BarChartProps> = ({
  data,
  showDataLabels = false,
  labelsMode = 'values',
  containerHeight: propContainerHeight,
  maxCategories = 100
}) => {
  const chartRef = useRef<any>(null);
  const [showAllLegends, setShowAllLegends] = useState(false);
  const [itemsPerRow, setItemsPerRow] = useState(3);
  const [selectedSeries, setSelectedSeries] = useState<Set<number>>(new Set());

  const colorPalette = [
    "#53eafd",
    "#ffdf20",
    "#bbf451",
    "#ffa2a2",
    "#c4b4ff",
    "#8ec5ff",
    "#ffb86a",
    "#7bf1a8",
    "#fda5d5",
    "#46ecd5",
    "#ffd230",
    "#a3b3ff",
    "#ffa1ad",
    "#5ee9b5",
    "#dab2ff",
    "#74d4ff",
    "#f4a8ff"
  ];

  const sortedData = useMemo(() => {
    return sortDataByValue(data, maxCategories);
  }, [data, maxCategories]);

  // Calculate how many legend items can fit per row
  const calculateItemsPerRow = (seriesData: any[]) => {
    const container = document.querySelector('#legend-container-bar') as HTMLElement;
    if (!container || seriesData.length === 0) {
      setItemsPerRow(3);
      return;
    }

    const tempContainer = document.createElement('div');
    tempContainer.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      font-family: Inter;
      font-size: 13px;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: 0.32px;
    `;
    document.body.appendChild(tempContainer);

    let itemsThatFit = 0;
    const containerWidth = container.offsetWidth;

    // First, measure the "Select All" button width (only when items are hidden)
    const allButton = document.createElement('div');
    allButton.style.cssText = `
      display: inline-flex;
      align-items: center;
      gap: 10px;
      margin-right: 15px;
      font-family: Inter;
      font-size: 13px;
      font-weight: 500;
      color: #4F46E5;
    `;
    allButton.innerHTML = `
      <span style="width: 15px; height: 15px; border-radius: 50%; border: 2px solid #4F46E5; background-color: transparent;"></span>
      <span>Select All</span>
    `;
    tempContainer.appendChild(allButton);
    const allButtonWidth = tempContainer.offsetWidth;
    tempContainer.innerHTML = ''; // Clear the container

    // Then, measure the "See more" button width to reserve space
    const seeMoreButton = document.createElement('div');
    seeMoreButton.style.cssText = `
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px;
      margin-right: 15px;
      font-family: Inter;
      font-size: 13px;
      color: #6b7280;
    `;
    seeMoreButton.textContent = `See more (${seriesData.length})`;
    tempContainer.appendChild(seeMoreButton);
    const seeMoreButtonWidth = tempContainer.offsetWidth;
    tempContainer.innerHTML = ''; // Clear the container

    // Now calculate how many legend items fit with both buttons reserved
    const availableWidth = containerWidth - allButtonWidth - seeMoreButtonWidth;

    // If we have many series, be more conservative with the calculation
    if (seriesData.length > 3) {
      // For many series, show fewer items initially to ensure "See more" button appears
      const conservativeCount = Math.max(2, Math.floor(availableWidth / 150)); // More conservative: ~150px per item
      setItemsPerRow(Math.min(conservativeCount, seriesData.length - 1)); // Always leave room for "See more"
      document.body.removeChild(tempContainer);
      return;
    }

    for (let i = 0; i < seriesData.length; i++) {
      const series = seriesData[i];
      const itemText = series.name;

      // Create a temporary element to measure text width
      const tempItem = document.createElement('div');
      tempItem.style.cssText = `
        display: inline-flex;
        align-items: center;
        gap: 10px;
        margin-right: 15px;
        font-family: Inter;
        font-size: 13px;
        font-weight: 400;
        line-height: 18px;
        letter-spacing: 0.32px;
      `;
      tempItem.innerHTML = `
        <span style="width: 15px; height: 15px; border-radius: 50%; background-color: ${colorPalette[i % colorPalette.length]};"></span>
        <span>${itemText}</span>
      `;
      tempContainer.appendChild(tempItem);

      const itemWidth = tempContainer.offsetWidth;

      if (itemWidth <= availableWidth) {
        itemsThatFit = i + 1;
      } else {
        break;
      }

      tempContainer.innerHTML = ''; // Clear for next iteration
    }

    document.body.removeChild(tempContainer);

    // Ensure at least 1 item can fit, and at most all items
    // If we have many series, be more conservative to ensure "See more" button shows
    const maxItems = seriesData.length > 3 ? Math.max(2, seriesData.length - 1) : seriesData.length;
    const finalCount = Math.max(1, Math.min(itemsThatFit, maxItems));
    setItemsPerRow(finalCount);
  };

  // Toggle series selection with improved UX
  const toggleSeriesSelection = (seriesIndex: number) => {
    const totalSeries = sortedData.series.length;
    const newSelectedSeries = new Set(selectedSeries);

    // Case 1: Nothing selected (all visible) - clicking one item isolates it
    if (selectedSeries.size === 0) {
      newSelectedSeries.add(seriesIndex);
    }
    // Case 2: All items are selected - clicking one item isolates it
    else if (selectedSeries.size === totalSeries) {
      newSelectedSeries.clear();
      newSelectedSeries.add(seriesIndex);
    }
    // Case 3: Only this item is selected - clicking it shows all (clears selection)
    else if (selectedSeries.size === 1 && selectedSeries.has(seriesIndex)) {
      newSelectedSeries.clear();
    }
    // Case 4: Some items selected, toggle this one
    else {
      if (newSelectedSeries.has(seriesIndex)) {
        newSelectedSeries.delete(seriesIndex);
      } else {
        newSelectedSeries.add(seriesIndex);
      }
    }

    setSelectedSeries(newSelectedSeries);
  };

  // Select all series (clears selection to show all)
  const selectAllSeries = () => {
    setSelectedSeries(new Set()); // Empty set = all visible
  };

  // Calculate items per row when data changes
  useEffect(() => {
    if (sortedData && sortedData.series) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        calculateItemsPerRow(sortedData.series);
      }, 100);
    }
  }, [sortedData]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      if (sortedData && sortedData.series) {
        calculateItemsPerRow(sortedData.series);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sortedData]);

  // Additional calculation after component mounts
  useEffect(() => {
    if (sortedData && sortedData.series) {
      const timer = setTimeout(() => {
        calculateItemsPerRow(sortedData.series);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [sortedData]);

  if (!sortedData || !sortedData.series || sortedData.series.length === 0) {
    return <div className="text-center p-4 pt-36 pb-36">No data available for chart</div>;
  }

  const calculateChartDimensions = () => {
    const isStackChart = sortedData.type === 'stackedColumn';
    const CONTAINER_HEIGHT = propContainerHeight || 400;

    if (isStackChart) {

      const categoryCount = sortedData.categories.length;
      const barHeight = 60;
      const totalHeight = categoryCount * barHeight + 150;
      const needsScrolling = totalHeight > CONTAINER_HEIGHT;

      return {
        chartHeight: needsScrolling ? totalHeight : CONTAINER_HEIGHT,
        containerHeight: CONTAINER_HEIGHT,
        needsScrolling
      };
    } else {
      // For regular charts, use categories count
      const itemCount = sortedData.categories.length;
      const barHeight = 35; // Height per bar
      const totalHeight = itemCount * barHeight + 100;
      const needsScrolling = totalHeight > CONTAINER_HEIGHT;

      return {
        chartHeight: needsScrolling ? totalHeight : CONTAINER_HEIGHT,
        containerHeight: CONTAINER_HEIGHT,
        needsScrolling
      };
    }
  };

  // Calculate dynamic bar width and spacing based on container width
  const calculateBarDimensions = () => {
    const containerWidth = 720; // Use container width instead of fixed canvas width
    const n = sortedData.categories.length; // Number of categories
    const spacingBetweenBars = 12; // Fixed 12px spacing between bars
    const minBarWidth = 20; // Minimum bar width
    const maxBarWidth = 60; // Maximum bar width

    // Calculate raw bar width: [containerWidth - (n+1) * spacing] / n
    const totalSpacing = (n + 1) * spacingBetweenBars;
    const availableWidth = containerWidth - totalSpacing;
    const rawBarWidth = availableWidth / n;

    let finalBarWidth = rawBarWidth;

    // Apply constraints - but always fit within container (no horizontal scroll)
    if (rawBarWidth < minBarWidth) {
      // If bar width is too small, use minimum width but reduce spacing if needed
      finalBarWidth = minBarWidth;
    } else if (rawBarWidth > maxBarWidth) {
      // If bar width is too large, cap it at maximum width
      finalBarWidth = maxBarWidth;
    }

    // Always use container width - no horizontal scrolling
    const totalChartWidth = containerWidth;

    return {
      barWidth: finalBarWidth,
      spacing: spacingBetweenBars,
      needsHorizontalScroll: false, // Always false - no horizontal scroll
      totalChartWidth,
      canvasWidth: containerWidth
    };
  };

  // Create data labels configuration
  const createDataLabelsConfig = () => {
    if (!showDataLabels || labelsMode === 'none') {
      return { display: false };
    }

    return {
      display: true,
      anchor: 'end' as const,
      align: 'right' as const,
      offset: 4,
      color: '#333',
      backgroundColor: 'transparent',
      borderColor: 'none',
      borderWidth: 0,
      borderRadius: 0,
      padding: 4,
      font: {
        size: 10
      },
      formatter: (value: number, context: any) => {
        const categoryTotal = context.chart.data.datasets.reduce((sum: number, dataset: any) => {
          return sum + (dataset.data[context.dataIndex] || 0);
        }, 0);

        const percentage = (value / categoryTotal) * 100;

        if (percentage < 15) {
          return '';
        }

        switch (labelsMode) {
          case 'values':
            return formatValue(value);
          case 'percentages':
            return percentage.toFixed(1) + '%';
          case 'both':
            return formatValue(value) + '\n(' + percentage.toFixed(1) + '%)';
          default:
            return formatValue(value);
        }
      }
    };
  };

  // Prepare chart data based on chart type
  const prepareChartData = () => {
    const isStackChart = sortedData.type === 'stackedColumn';

    if (isStackChart) {
      // For stacked charts, group by stack value to create multiple stacked bars
      const stackGroups = new Map<string, ChartSeries[]>();

      // Group series by their stack value
      sortedData.series.forEach((series: ChartSeries) => {
        const stackName = series.stack || 'default';
        if (!stackGroups.has(stackName)) {
          stackGroups.set(stackName, []);
        }
        stackGroups.get(stackName)!.push(series);
      });

      // Create datasets for each stack group
      const datasets: any[] = [];
      let colorIndex = 0;

      stackGroups.forEach((seriesInStack, stackName) => {
        seriesInStack.forEach((series, seriesIndex) => {
          datasets.push({
            label: series.name,
            data: series.data,
            backgroundColor: colorPalette[colorIndex % colorPalette.length] + '80',
            borderColor: colorPalette[colorIndex % colorPalette.length],
            borderWidth: 1,
            stack: stackName, // Use the actual stack name from data
            hidden: selectedSeries.size > 0 && !selectedSeries.has(seriesIndex), // Hide if some are selected and this one is not
            // Ensure tooltip uses the solid color (without transparency)
            hoverBackgroundColor: colorPalette[colorIndex % colorPalette.length],
            hoverBorderColor: colorPalette[colorIndex % colorPalette.length]
          });
          colorIndex++;
        });
      });

      return {
        labels: sortedData.categories,
        datasets: datasets
      };
    } else {
      // Simple horizontal bar chart
      const datasets = sortedData.series.map((series, index) => ({
        label: series.name,
        data: series.data,
        backgroundColor: colorPalette[index % colorPalette.length] + '80',
        borderColor: colorPalette[index % colorPalette.length],
        borderWidth: 1,
        hidden: selectedSeries.size > 0 && !selectedSeries.has(index), // Hide if some are selected and this one is not
        // Ensure tooltip uses the solid color (without transparency)
        hoverBackgroundColor: colorPalette[index % colorPalette.length],
        hoverBorderColor: colorPalette[index % colorPalette.length]
      }));

      return {
        labels: sortedData.categories,
        datasets: datasets
      };
    }
  };

  const isStackChart = sortedData.type === 'stackedColumn';
  const { chartHeight, containerHeight, needsScrolling } = calculateChartDimensions();
  const { barWidth, spacing, needsHorizontalScroll, totalChartWidth } = calculateBarDimensions();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    layout: {
      padding: {
        bottom: 5,
        left: 10,
        right: 28,
        top: 20
      }
    },
    elements: {
      bar: {
        borderWidth: 1,
        // Apply calculated bar width
        borderSkipped: false,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        position: 'top' as const,
        stacked: isStackChart,
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)'
        },
        ticks: {
          color: '#424242',
          font: {
            size: propContainerHeight && propContainerHeight > 500 ? 11 : 10,
          },
          callback: function (value: any) {
            return formatValue(Number(value));
          }
        },
        title: {
          display: !!sortedData.yAxisTitle,
          text: sortedData.yAxisTitle,
          color: '#424242',
          font: {
            size: 13,
            lineHeight: 1.8
          }
        }
      },
      y: {
        beginAtZero: true,
        stacked: isStackChart,
        // Calculate category and bar percentages based on our spacing requirements
        categoryPercentage: (barWidth / (barWidth + spacing)) * 100 / 100, // Convert to decimal
        barPercentage: 1.0, // Use full available space within category
        grid: {
          display: false,
        },
        ticks: {
          color: '#424242',
          font: {
            size: propContainerHeight && propContainerHeight > 500 ? 13 : 12,
          },
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: function (value: any, index: number): string {
            // Access the chart instance from the context
            const chart = (this as any).chart;
            if (!chart || !chart.data || !chart.data.labels || !Array.isArray(chart.data.labels)) {
              return '';
            }

            const label: string = chart.data.labels[index] as string;
            if (!label || typeof label !== 'string') {
              return '';
            }

            if (label.length > 30) {
              return label.substring(0, 30) + '...';
            }
            return label;
          }
        },
        title: {
          display: !!sortedData.xAxisTitle,
          text: sortedData.xAxisTitle,
          color: '#424242',
          font: {
            size: 13,
            lineHeight: 1.8
          }
        }
      },
    },
    plugins: {
      legend: {
        display: false // Completely disable built-in legend
      },
      title: {
        display: false, // Disable built-in title, use HTML title instead
        text: sortedData.title,
        align: 'center' as const,
        font: {
          size: 12
        },
        padding: {
          bottom: 25,
          left: 0,
          right: 0,
          top: 0
        }
      },
      tooltip: {
        displayColors: true, // Enable colors but we'll customize them
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        maxHeight: 200,
        bodySpacing: 4,
        titleSpacing: 4,
        footerSpacing: 4,
        cornerRadius: 6,
        filter: function (tooltipItem: any) {
          return tooltipItem.parsed.x > 0;
        },
        mode: 'nearest' as const,
        usePointStyle: true, // This forces circles instead of squares
        callbacks: {
          title: function (context: any[]) {
            return context[0]?.label || '';
          },
          label: function (context: any) {
            const value = context.parsed.x;
            if (value === 0) return '';

            const formattedValue = formatValue(value);
            const legendLabel = context.dataset.label;

            // Always show the dataset label (series name) for clarity
            return `${legendLabel}: ${formattedValue}`;
          },
          labelColor: function (context: any) {
            // Return the correct color for the tooltip circle
            const datasetIndex = context.datasetIndex;
            const colorIndex = datasetIndex % colorPalette.length;
            const seriesColor = colorPalette[colorIndex];

            return {
              borderColor: seriesColor,
              backgroundColor: seriesColor
            };
          },
          afterLabel: function (context: any) {
            const value = context.parsed.x;
            if (value === 0) return '';

            if (isStackChart) {
              const categoryTotal = context.chart.data.datasets.reduce((sum: number, dataset: any) => {
                return sum + (dataset.data[context.dataIndex] || 0);
              }, 0);
              const percentage = ((context.parsed.x / categoryTotal) * 100).toFixed(1);
              return `${percentage}% of ${context.label} total`;
            }
            return '';
          },
        }
      },
      datalabels: createDataLabelsConfig()
    },
    interaction: {
      intersect: true,
      mode: 'nearest' as const,
    },
  };

  return (
    <>
      <div className="w-full">
        {/* Chart Title */}
        {/* {sortedData.title && (
          <div className="text-h4 text-primary-text mt-2 mb-4 text-left">{sortedData.title}</div>
        )} */}
        {/* React-based Legend - After title, before chart */}
        {sortedData.showLegend !== false && (
          <div id="legend-container-bar" className="mb-4">
            <ul key={showAllLegends ? 'expanded' : 'collapsed'} className="flex" style={{
              flexWrap: showAllLegends ? 'wrap' : 'nowrap',
              overflow: showAllLegends ? 'visible' : 'hidden',
              gap: '8px'
            }}>
              {(() => {
                const itemsToShow = showAllLegends ? sortedData.series.length : itemsPerRow;
                const hasMoreItems = sortedData.series.length > itemsPerRow;
                const hasSelectedItems = selectedSeries.size > 0;
                const allItemsSelected = selectedSeries.size === sortedData.series.length;
                const shouldShowSelectAll = hasSelectedItems && !allItemsSelected;
                // If nothing is selected, treat all as selected (default state)
                const effectiveSelectedSeries = selectedSeries.size > 0 ? selectedSeries : new Set(sortedData.series.map((_, index) => index));

                return (
                  <>
                    {/* "Select All" option - only show when some (but not all) items are selected */}
                    {shouldShowSelectAll && (
                      <li
                        className="flex items-center gap-1 cursor-pointer"
                        onClick={selectAllSeries}
                      >
                        <span
                          className="w-4 h-4 rounded-full flex-shrink-0 border-2"
                          style={{
                            backgroundColor: 'transparent',
                            borderColor: '#4F46E5',
                            borderRadius: '50%'
                          }}
                        />
                        <span
                          className="text-sm font-medium whitespace-nowrap"
                          style={{
                            fontFamily: 'Inter',
                            fontWeight: '500',
                            fontSize: '13px',
                            lineHeight: '18px',
                            letterSpacing: '0.32px',
                            color: '#4F46E5'
                          }}
                        >
                          Select All
                        </span>
                      </li>
                    )}

                    {sortedData.series.slice(0, itemsToShow).map((series, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-1 cursor-pointer"
                        onClick={() => toggleSeriesSelection(index)}
                      >
                        <span
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: colorPalette[index % colorPalette.length],
                            opacity: effectiveSelectedSeries.has(index) ? 1 : 0.5
                          }}
                        />
                        <span
                          className="text-sm whitespace-nowrap"
                          style={{
                            fontFamily: 'Inter',
                            fontWeight: '400',
                            fontSize: '13px',
                            lineHeight: '18px',
                            letterSpacing: '0.32px',
                            color: effectiveSelectedSeries.has(index) ? '#242424' : '#aaa',
                            textDecoration: 'none'
                          }}
                        >
                          {series.name}
                        </span>
                      </li>
                    ))}

                    {hasMoreItems && !showAllLegends && (
                      <li
                        className="flex items-center gap-1 cursor-pointer px-2 rounded-full text-sm transition-colors flex-shrink-0"
                        style={{
                          color: '#6b7280',
                          fontFamily: 'Inter',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6e6e6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => setShowAllLegends(true)}
                      >
                        <span>See more ({sortedData.series.length - itemsPerRow})</span>
                        <span style={{ fontSize: '10px' }}>▼</span>
                      </li>
                    )}
                    {hasMoreItems && showAllLegends && (
                      <li
                        className="flex items-center gap-1 cursor-pointer px-2 rounded-full text-sm transition-colors flex-shrink-0"
                        style={{
                          color: '#6b7280',
                          fontFamily: 'Inter',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6e6e6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => setShowAllLegends(false)}
                      >
                        <span>See less</span>
                        <span style={{ fontSize: '10px' }}>▲</span>
                      </li>
                    )}
                  </>
                );
              })()}
            </ul>
          </div>
        )}
        <div
          className="w-full"
          style={{
            height: `${containerHeight}px`,
            overflowY: needsScrolling ? 'auto' : 'hidden',
            overflowX: 'hidden', // Always hidden - no horizontal scroll
            marginRight: '60px', // Extra margin to clear the scroll bar and show text values
          }}
        >
          <div
            style={{
              height: `${chartHeight}px`,
              width: '100%', // Always use full container width
              minWidth: 'auto',
            }}
          >
            <Bar
              ref={chartRef}
              data={prepareChartData()}
              options={options}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default BarChart;